import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { DEFAULT_ACCOUNT_DATA_MAX_AGE_MS } from "../config/accountDataConfig";
import { accountSummaryService } from "../services/accountSummaryService";
import { IbkrAccountSummary, IbkrPositionV2, IbkrPositionV2History } from "../types";

// Query key factory for account-related queries
export const accountQueryKeys = {
  all: ["account"] as const,
  positions: (adminViewUserId?: string) =>
    adminViewUserId
      ? (["account", "positionsV2", "admin", adminViewUserId] as const)
      : (["account", "positionsV2", "user"] as const),
  currentUserPositions: (authenticatedUserId: string) =>
    ["account", "positionsV2", "user", authenticatedUserId] as const,
  summary: (adminViewUserId?: string) =>
    adminViewUserId
      ? (["account", "summary", "admin", adminViewUserId] as const)
      : (["account", "summary", "user"] as const),
  currentUserSummary: (authenticatedUserId: string) =>
    ["account", "summary", "user", authenticatedUserId] as const,
  positionHistory: (userId: string, limit?: number) => ["account", "positionHistoryV2", userId, limit] as const,
};

/**
 * Hook to fetch positions V2 data.
 * Uses admin endpoint when adminViewUserId is provided.
 * @param adminViewUserId - admin user ID (undefined = current user)
 * @param enabled - when false, query is disabled and no API call is made (default: true)
 */
export function usePositions(adminViewUserId?: string, enabled: boolean = true) {
  const { user, effectiveUserId } = useAuth();
  const accountOwnerUserId = effectiveUserId === undefined
    ? user?.id ?? ""
    : effectiveUserId ?? "";

  return useQuery<IbkrPositionV2[], Error>({
    queryKey: adminViewUserId
      ? accountQueryKeys.positions(adminViewUserId)
      : accountQueryKeys.currentUserPositions(accountOwnerUserId),
    queryFn: async ({ signal }) => {
      if (adminViewUserId) {
        return accountSummaryService.getAdminUserPositionsV2(
          adminViewUserId,
          DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
          signal
        );
      }
      return accountSummaryService.getPositionsV2(DEFAULT_ACCOUNT_DATA_MAX_AGE_MS, signal);
    },
    staleTime: 30 * 1000,
    enabled: enabled && (!!adminViewUserId || !!accountOwnerUserId),
  });
}

/**
 * Hook to fetch account summary data.
 * Uses admin endpoint when adminViewUserId is provided.
 * @param adminViewUserId - admin user ID (undefined = current user)
 * @param enabled - when false, query is disabled and no API call is made (default: true)
 */
export function useAccountSummary(adminViewUserId?: string, enabled: boolean = true) {
  const { user, effectiveUserId } = useAuth();
  const accountOwnerUserId = effectiveUserId === undefined
    ? user?.id ?? ""
    : effectiveUserId ?? "";

  return useQuery<IbkrAccountSummary, Error>({
    queryKey: adminViewUserId
      ? accountQueryKeys.summary(adminViewUserId)
      : accountQueryKeys.currentUserSummary(accountOwnerUserId),
    queryFn: async ({ signal }) => {
      if (adminViewUserId) {
        return accountSummaryService.getAdminUserAccountSummary(
          adminViewUserId,
          DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
          false,
          signal
        );
      }
      return accountSummaryService.getAccountSummary(DEFAULT_ACCOUNT_DATA_MAX_AGE_MS, signal);
    },
    staleTime: 30 * 1000,
    enabled: enabled && (!!adminViewUserId || !!accountOwnerUserId),
  });
}

/**
 * Hook to fetch position V2 history (Admin only).
 * @param userId - The target user's UUID
 * @param limit - Maximum number of history records (default: 100)
 */
export function usePositionHistory(userId: string, limit: number = 100) {
  return useQuery<{ history: IbkrPositionV2History[]; count: number }, Error>({
    queryKey: accountQueryKeys.positionHistory(userId, limit),
    queryFn: ({ signal }) =>
      accountSummaryService.getAdminUserPositionsV2History(userId, limit, signal),
    enabled: !!userId,
  });
}
